"use client";
import { Helmet } from "react-helmet";

import { useEffect, useRef, useState } from "react";

/* ── Reveal on scroll ── */
function useReveal(threshold = 0.1) {
  const ref = useRef(null);
  const [v, setV] = useState(false);
  useEffect(() => {
    const o = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setV(true); o.disconnect(); } }, { threshold });
    if (ref.current) o.observe(ref.current);
    return () => o.disconnect();
  }, []);
  return [ref, v];
}
function Reveal({ children, delay = 0, className = "", style = {} }) {
  const [ref, v] = useReveal();
  return (
    <div ref={ref} className={className} style={{ opacity: v ? 1 : 0, transform: v ? "none" : "translateY(36px)", transition: `opacity .75s ease ${delay}ms, transform .75s ease ${delay}ms`, ...style }}>
      {children}
    </div>
  );
}

/* ── Marquee ── */
function Marquee({ items, speed = 40, reverse = false }) {
  return (
    <div style={{ overflow: "hidden", whiteSpace: "nowrap", userSelect: "none" }}>
      <div style={{ display: "inline-flex", animation: `wdMarquee${reverse ? "R" : ""} ${speed}s linear infinite` }}>
        {[...Array(4)].map((_, i) => (
          <span key={i} style={{ display: "inline-flex" }}>
            {items.map((item, j) => (
              <span key={j} style={{ display: "inline-flex", alignItems: "center", gap: 18, padding: "0 24px" }}>
                <span style={{ color: "rgba(255,255,255,.07)", fontSize: "clamp(28px,4vw,56px)", fontWeight: 900, letterSpacing: "-1px", textTransform: "uppercase", fontFamily: "'Syne',sans-serif" }}>{item}</span>
                <span style={{ width: 7, height: 7, borderRadius: "50%", background: "rgba(99,102,241,.35)", flexShrink: 0 }} />
              </span>
            ))}
          </span>
        ))}
      </div>
    </div>
  );
}

/* ── animated counter ── */
function Counter({ end, suffix = "", duration = 2000, active }) {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!active) return;
    let s = null;
    const f = (ts) => {
      if (!s) s = ts;
      const p = Math.min((ts - s) / duration, 1);
      setN(Math.floor((1 - Math.pow(1 - p, 3)) * end));
      if (p < 1) requestAnimationFrame(f);
    };
    requestAnimationFrame(f);
  }, [active]);
  return <>{n}{suffix}</>;
}

/* ── Canvas particles ── */
function Particles() {
  const ref = useRef(null);
  useEffect(() => {
    const c = ref.current; if (!c) return;
    const ctx = c.getContext("2d");
    const resize = () => { c.width = c.offsetWidth; c.height = c.offsetHeight; };
    resize(); window.addEventListener("resize", resize);
    const pts = Array.from({ length: 40 }, () => ({ x: Math.random() * c.width, y: Math.random() * c.height, r: Math.random() * 1.1 + .3, vx: (Math.random() - .5) * .18, vy: (Math.random() - .5) * .18, o: Math.random() * .28 + .05 }));
    let raf;
    const loop = () => {
      ctx.clearRect(0, 0, c.width, c.height);
      pts.forEach(p => {
        p.x = (p.x + p.vx + c.width) % c.width; p.y = (p.y + p.vy + c.height) % c.height;
        ctx.beginPath(); ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(148,163,184,${p.o})`; ctx.fill();
      });
      raf = requestAnimationFrame(loop);
    };
    loop();
    return () => { cancelAnimationFrame(raf); window.removeEventListener("resize", resize); };
  }, []);
  return <canvas ref={ref} style={{ position: "absolute", inset: 0, width: "100%", height: "100%", pointerEvents: "none", zIndex: 0 }} />;
}

/* ── Browser mockup ── */
function BrowserMock() {
  return (
    <div style={{ borderRadius: 18, overflow: "hidden", border: "1px solid rgba(255,255,255,.1)", boxShadow: "0 48px 120px rgba(0,0,0,.7), 0 0 0 1px rgba(99,102,241,.1)" }}>
      {/* chrome bar */}
      <div style={{ background: "rgba(18,22,52,.97)", padding: "12px 18px", display: "flex", alignItems: "center", gap: 10, borderBottom: "1px solid rgba(255,255,255,.06)" }}>
        <div style={{ display: "flex", gap: 6 }}>
          {["#ef4444", "#f59e0b", "#22c55e"].map((c, i) => <div key={i} style={{ width: 10, height: 10, borderRadius: "50%", background: c, opacity: .75 }} />)}
        </div>
        <div style={{ flex: 1, background: "rgba(255,255,255,.05)", borderRadius: 8, padding: "5px 12px", fontSize: 11, color: "#4b5563", fontFamily: "monospace", display: "flex", alignItems: "center", gap: 5 }}>
          <span style={{ color: "#22c55e", fontSize: 10 }}>🔒</span> yourclient.com
        </div>
      </div>
      {/* page preview */}
      <div style={{ background: "#0b0e20", padding: 20, minHeight: 300 }}>
        {/* nav */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20, padding: "8px 0", borderBottom: "1px solid rgba(255,255,255,.05)" }}>
          <div style={{ width: 60, height: 8, background: "linear-gradient(90deg,#818cf8,#a78bfa)", borderRadius: 4 }} />
          <div style={{ display: "flex", gap: 12 }}>
            {[50, 40, 46, 34].map((w, i) => <div key={i} style={{ width: w, height: 6, background: "rgba(255,255,255,.08)", borderRadius: 3 }} />)}
          </div>
          <div style={{ width: 60, height: 24, background: "linear-gradient(135deg,#4f46e5,#7c3aed)", borderRadius: 6 }} />
        </div>
        {/* hero */}
        <div style={{ background: "linear-gradient(135deg,rgba(99,102,241,.12),rgba(168,85,247,.06))", borderRadius: 12, padding: "24px 18px", marginBottom: 14, border: "1px solid rgba(99,102,241,.1)" }}>
          <div style={{ width: 50, height: 3, background: "linear-gradient(90deg,#818cf8,transparent)", borderRadius: 99, marginBottom: 12 }} />
          <div style={{ height: 16, width: "65%", background: "rgba(255,255,255,.14)", borderRadius: 5, marginBottom: 7 }} />
          <div style={{ height: 12, width: "45%", background: "rgba(255,255,255,.07)", borderRadius: 5, marginBottom: 16 }} />
          <div style={{ display: "flex", gap: 8 }}>
            <div style={{ height: 28, width: 80, background: "linear-gradient(135deg,#4f46e5,#7c3aed)", borderRadius: 7 }} />
            <div style={{ height: 28, width: 60, background: "rgba(255,255,255,.06)", borderRadius: 7 }} />
          </div>
        </div>
        {/* cards */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 10 }}>
          {["#818cf8", "#34d399", "#f59e0b"].map((c, i) => (
            <div key={i} style={{ background: "rgba(255,255,255,.03)", borderRadius: 10, padding: 12, border: `1px solid ${c}20` }}>
              <div style={{ width: 22, height: 22, borderRadius: 6, background: `${c}18`, border: `1px solid ${c}33`, marginBottom: 8 }} />
              <div style={{ height: 5, width: "80%", background: "rgba(255,255,255,.1)", borderRadius: 3, marginBottom: 4 }} />
              <div style={{ height: 4, width: "55%", background: "rgba(255,255,255,.05)", borderRadius: 3 }} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════ */
export default function WebsiteDevelopmentPage() {
  const statsRef = useRef(null);
  const [statsOn, setStatsOn] = useState(false);
  const [mouseXY, setMouseXY] = useState({ x: 0, y: 0 });
  const [orb, setOrb] = useState({ x: 0, y: 0 });
  const [activeService, setActiveService] = useState(0);

  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setStatsOn(true); }, { threshold: .2 });
    if (statsRef.current) obs.observe(statsRef.current);
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    const h = (e) => setMouseXY({ x: e.clientX, y: e.clientY });
    window.addEventListener("mousemove", h);
    return () => window.removeEventListener("mousemove", h);
  }, []);

  useEffect(() => {
    const id = setInterval(() => setOrb(o => ({ x: o.x + (mouseXY.x - o.x) * .05, y: o.y + (mouseXY.y - o.y) * .05 })), 16);
    return () => clearInterval(id);
  }, [mouseXY]);

  useEffect(() => {
    const el = document.createElement("style");
    el.textContent = `
      @import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;700;800;900&family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;0,9..40,600&display=swap');
      *{box-sizing:border-box}
      @keyframes wdMarquee  { from{transform:translateX(0)} to{transform:translateX(-50%)} }
      @keyframes wdMarqueeR { from{transform:translateX(-50%)} to{transform:translateX(0)} }
      @keyframes wdFloat    { 0%,100%{transform:translateY(0) rotate(0deg)} 45%{transform:translateY(-16px) rotate(.6deg)} }
      @keyframes wdFloat2   { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-20px)} }
      @keyframes wdSpin     { from{transform:rotate(0)} to{transform:rotate(360deg)} }
      @keyframes wdSpinR    { from{transform:rotate(0)} to{transform:rotate(-360deg)} }
      @keyframes wdPing     { 0%{transform:scale(1);opacity:.6} 100%{transform:scale(2.5);opacity:0} }
      @keyframes wdPulse    { 0%,100%{opacity:1} 50%{opacity:.35} }
      @keyframes wdShimmer  { 0%{background-position:-200% center} 100%{background-position:200% center} }
      @keyframes wdMorph    { 0%,100%{background-position:0 50%} 50%{background-position:100% 50%} }
      @keyframes wdFadeUp   { from{opacity:0;transform:translateY(28px)} to{opacity:1;transform:none} }
      @keyframes wdScanline { 0%{top:-5%} 100%{top:105%} }

      .wd-display { font-family:'Syne',sans-serif!important; }
      .wd-body    { font-family:'DM Sans',sans-serif!important; }
      .wd-shimmer { background:linear-gradient(90deg,#818cf8,#a78bfa,#60a5fa,#818cf8);background-size:260% auto;-webkit-background-clip:text;background-clip:text;color:transparent;animation:wdShimmer 3s linear infinite }
      .wd-morph   { background:linear-gradient(135deg,#4f46e5,#7c3aed,#0ea5e9,#4f46e5)!important;background-size:300% 300%!important;animation:wdMorph 4s ease infinite!important }
      .wd-float   { animation:wdFloat 8s ease-in-out infinite }
      .wd-float2  { animation:wdFloat2 10s ease-in-out infinite }

      .wd-h1a { animation:wdFadeUp .9s ease .05s forwards;opacity:0 }
      .wd-h1b { animation:wdFadeUp .9s ease .2s  forwards;opacity:0 }
      .wd-h1c { animation:wdFadeUp .9s ease .35s forwards;opacity:0 }
      .wd-hp  { animation:wdFadeUp .9s ease .5s  forwards;opacity:0 }
      .wd-hb  { animation:wdFadeUp .9s ease .65s forwards;opacity:0 }
      .wd-hst { animation:wdFadeUp .9s ease .8s  forwards;opacity:0 }

      .wd-tab { transition:all .3s ease;border-left:2px solid rgba(255,255,255,.05);padding:15px 20px;cursor:pointer;display:flex;align-items:center;gap:10px; }
      .wd-tab:hover { background:rgba(99,102,241,.05);border-color:rgba(99,102,241,.25); }
      .wd-tab.act { background:rgba(99,102,241,.1);border-color:#6366f1; }

      .wd-step { transition:all .35s ease; }
      .wd-step:hover { background:rgba(99,102,241,.07)!important;border-color:rgba(99,102,241,.35)!important;transform:translateY(-5px); }

      .wd-tech { transition:all .3s ease;cursor:default; }
      .wd-tech:hover { background:rgba(99,102,241,.15)!important;border-color:rgba(99,102,241,.45)!important;color:#c7d2fe!important;transform:translateY(-4px) scale(1.04); }

      .wd-why { transition:all .35s ease;cursor:default; }
      .wd-why:hover { background:rgba(99,102,241,.07)!important;border-color:rgba(99,102,241,.35)!important;transform:translateY(-4px); }

      .wd-cta-p { transition:all .4s ease; }
      .wd-cta-p:hover { transform:scale(1.05)!important;box-shadow:0 24px 70px rgba(79,70,229,.45)!important; }
      .wd-cta-s { transition:all .3s ease; }
      .wd-cta-s:hover { border-color:rgba(167,139,250,.45)!important;color:#fff!important;background:rgba(99,102,241,.07)!important; }

      /* ── RESPONSIVE ── */
      .wd-hero    { padding:80px 60px 0;min-height:100vh;display:flex;flex-direction:column;position:relative; }
      .wd-wrap    { max-width:1240px;margin:0 auto;width:100%; }
      .wd-sec     { padding:110px 60px; }
      .wd-sec-sm  { padding:72px 60px; }

      .wd-hero-grid  { display:grid;grid-template-columns:1fr 1fr;gap:48px;align-items:center;flex:1;padding-bottom:60px;margin-top:28px; }
      .wd-svc-layout { display:grid;grid-template-columns:260px 1fr;border-radius:24px;overflow:hidden;border:1px solid rgba(255,255,255,.07); }
      .wd-svc-tabs   { display:flex;flex-direction:column;border-right:1px solid rgba(255,255,255,.06);background:rgba(8,10,22,.5); }
      .wd-proc-grid  { display:grid;grid-template-columns:repeat(4,1fr);gap:20px; }
      .wd-tech-row   { display:flex;flex-wrap:wrap;gap:10px;justify-content:center; }
      .wd-why-grid   { display:grid;grid-template-columns:1fr 1fr;gap:80px;align-items:center; }
      .wd-why-cards  { display:grid;grid-template-columns:1fr 1fr;gap:14px; }
      .wd-stats      { display:grid;grid-template-columns:repeat(4,1fr); }
      .wd-cta-btns   { display:flex;flex-wrap:wrap;justify-content:center;gap:16px; }
      .wd-topbar-r   { display:flex; }
      .wd-badge1,.wd-badge2,.wd-badge3 { display:block; }
      .wd-pills      { display:flex; }
      .wd-mini-stats { display:flex; }

      @media (max-width:1024px) {
        .wd-hero   { padding:60px 40px 0;min-height:auto; }
        .wd-sec    { padding:80px 40px; }
        .wd-sec-sm { padding:56px 40px; }
        .wd-hero-grid  { grid-template-columns:1fr;gap:44px;padding-bottom:48px; }
        .wd-svc-layout { grid-template-columns:220px 1fr; }
        .wd-proc-grid  { grid-template-columns:repeat(2,1fr); }
        .wd-why-grid   { grid-template-columns:1fr;gap:48px; }
        .wd-stats      { grid-template-columns:repeat(4,1fr); }
        .wd-topbar-r   { display:none; }
        .wd-badge1,.wd-badge2,.wd-badge3 { display:none; }
      }

      @media (max-width:768px) {
        .wd-hero   { padding:28px 20px 0; }
        .wd-sec    { padding:56px 20px; }
        .wd-sec-sm { padding:40px 20px; }
        .wd-hero-grid  { grid-template-columns:1fr;gap:32px;margin-top:16px; }
        .wd-svc-layout { grid-template-columns:1fr; }
        .wd-svc-tabs   { flex-direction:row;overflow-x:auto;border-right:none;border-bottom:1px solid rgba(255,255,255,.06); }
        .wd-tab        { border-left:none!important;border-bottom:2px solid transparent;padding:10px 14px!important;white-space:nowrap;flex-shrink:0; }
        .wd-tab.act    { border-bottom-color:#6366f1!important;background:rgba(99,102,241,.08)!important; }
        .wd-proc-grid  { grid-template-columns:1fr; }
        .wd-why-grid   { grid-template-columns:1fr;gap:32px; }
        .wd-why-cards  { grid-template-columns:1fr; }
        .wd-stats      { grid-template-columns:repeat(2,1fr); }
        .wd-cta-btns   { flex-direction:column;align-items:stretch; }
        .wd-cta-btns button { width:100%!important; }
        .wd-pills      { display:none!important; }
        .wd-mini-stats { display:none!important; }
        .wd-step:hover { transform:none!important; }
      }

      @media (max-width:480px) {
        .wd-stats     { grid-template-columns:repeat(2,1fr); }
        .wd-why-cards { grid-template-columns:1fr; }
        .wd-proc-grid { grid-template-columns:1fr; }
      }
    `;
    document.head.appendChild(el);
    return () => document.head.removeChild(el);
  }, []);

  const services = [
    { label: "Landing Pages",    icon: "🎯", color: "#818cf8", title: "High-Converting Landing Pages",   desc: "Sales-focused pages built to convert — A/B test-ready sections, optimised CTAs, lightning load times, and zero compromise on design quality.", features: ["Conversion-rate optimised", "A/B testing ready", "< 1s load target", "Analytics built-in"] },
    { label: "Corporate Sites",  icon: "🏢", color: "#60a5fa", title: "Corporate & Business Websites",   desc: "Authority-building multi-page websites for enterprises and mid-market companies. CMS-powered, brand-consistent, and SEO-ready from day one.", features: ["Multi-page architecture", "CMS integration", "Brand guidelines", "SEO foundation"] },
    { label: "E-Commerce",       icon: "🛒", color: "#34d399", title: "E-Commerce Storefronts",           desc: "Shopify, WooCommerce, or custom-built stores. Fast checkout, smart product filtering, payment gateways, and full inventory management.", features: ["Payment gateways", "Inventory system", "Mobile checkout", "Product filtering"] },
    { label: "Portfolios",       icon: "✦",  color: "#f59e0b", title: "Portfolio & Creative Websites",    desc: "Award-worthy portfolios for agencies, designers, and studios. Immersive animations, compelling case study layouts, unforgettable first impressions.", features: ["Immersive animations", "Case study layouts", "Gallery systems", "CMS-editable"] },
    { label: "SaaS Marketing",   icon: "🚀", color: "#ec4899", title: "SaaS Marketing Websites",          desc: "High-performance marketing sites for SaaS products with feature showcases, pricing tables, demo CTAs, and seamless product integrations.", features: ["Feature showcases", "Pricing tables", "Demo booking CTAs", "Product screenshots"] },
  ];

  const process = [
    { num: "01", color: "#818cf8", title: "Discovery",   desc: "We audit your goals, brand, competitors, and audience to build a clear strategy before we open Figma." },
    { num: "02", color: "#60a5fa", title: "Design",       desc: "Figma-first. You see exactly how it looks before we write a single line of code. Unlimited revisions included." },
    { num: "03", color: "#34d399", title: "Development",  desc: "Pixel-perfect implementation with clean code, fast performance, animations, and full CMS integration." },
    { num: "04", color: "#f59e0b", title: "Launch",        desc: "Deployment, domain, SSL, speed optimisation, and analytics setup. Then ongoing support and maintenance." },
  ];

  const techs = ["Next.js", "React", "Tailwind CSS", "Framer Motion", "Webflow", "WordPress", "Shopify", "Three.js", "GSAP", "Vercel", "Netlify", "Sanity CMS", "Figma", "TypeScript"];

  const why = [
    ["Performance First",    "Every site targets 90+ Lighthouse scores out of the box."],
    ["SEO Architecture",     "Technical SEO, structured data, and Core Web Vitals baked in."],
    ["CMS-Powered",          "Edit any content without touching code — Sanity, Contentful, or Payload."],
    ["Mobile-First Design",  "Designed mobile-first, tested across 30+ real devices before launch."],
    ["Accessibility",        "WCAG 2.1 AA compliant. Inclusive by default, not as an afterthought."],
    ["Post-Launch Care",     "Monthly retainers for updates, audits, and new feature rollouts."],
  ];

  const svc = services[activeService];

  return (
    <div className="wd-body" style={{ background: "#06070f", color: "#fff", overflow: "hidden" }}>

        
<Helmet>
  <title>
    Website Development Company in India | Custom Web Design Services | SoftCoders
  </title>

  <meta
    name="description"
    content="SoftCoders is a professional website development company in India building responsive business websites, ecommerce websites, portfolio websites, custom web solutions, and high-performance modern websites."
  />

  <meta
    name="keywords"
    content="
    website development company,
    web development company india,
    custom website development,
    responsive website design,
    business website development,
    ecommerce website development,
    website design company,
    professional website developer,
    corporate website development,
    startup website development,
    SEO friendly websites,
    modern website design,
    React website development,
    Next.js website development,
    custom web solutions,
    UI UX website design,
    portfolio website development,
    dynamic website development,
    web design services,
    SoftCoders
    "
  />

  <meta name="robots" content="index, follow" />
  <meta name="author" content="SoftCoders" />
  <meta name="language" content="English" />

  {/* Canonical */}
  <link
    rel="canonical"
    href="https://softcoders.in/website-development"
  />

  {/* Open Graph */}
  <meta
    property="og:title"
    content="Website Development Company | SoftCoders"
  />

  <meta
    property="og:description"
    content="Build responsive, SEO-friendly, and high-performance business websites with SoftCoders website development services."
  />

  <meta
    property="og:url"
    content="https://softcoders.in/website-development"
  />

  <meta property="og:type" content="website" />

  <meta
    property="og:image"
    content="https://softcoders.in/og-image.jpg"
  />

  {/* Twitter SEO */}
  <meta name="twitter:card" content="summary_large_image" />

  <meta
    name="twitter:title"
    content="Website Development Company | SoftCoders"
  />

  <meta
    name="twitter:description"
    content="SoftCoders develops responsive websites, ecommerce platforms, business websites, portfolio websites, and scalable web solutions."
  />

  <meta
    name="twitter:image"
    content="https://softcoders.in/og-image.jpg"
  />

  {/* GEO SEO */}
  <meta name="geo.region" content="IN-HR" />
  <meta name="geo.placename" content="Sirsa, Haryana" />

  {/* Mobile SEO */}
  <meta
    name="viewport"
    content="width=device-width, initial-scale=1"
  />

  {/* Theme */}
  <meta name="theme-color" content="#0a0d1f" />

  {/* Schema Markup */}
  <script type="application/ld+json">
    {`
      {
        "@context": "https://schema.org",
        "@type": "ProfessionalService",
        "name": "SoftCoders",
        "url": "https://softcoders.in",
        "description": "SoftCoders is a website development company providing responsive website design, ecommerce development, business websites, and custom web solutions.",
        "address": {
          "@type": "PostalAddress",
          "addressLocality": "Sirsa",
          "addressRegion": "Haryana",
          "addressCountry": "India"
        },
        "serviceType": [
          "Website Development",
          "Custom Website Design",
          "Business Website Development",
          "Ecommerce Website Development",
          "Responsive Web Design",
          "SEO Friendly Websites"
        ]
      }
    `}
  </script>
</Helmet>

      {/* mouse orb */}
      <div style={{ position: "fixed", pointerEvents: "none", zIndex: 0, width: 480, height: 480, borderRadius: "50%", left: orb.x - 240, top: orb.y - 240, background: "radial-gradient(circle,rgba(99,102,241,.055),transparent 70%)", transition: "left .08s,top .08s" }} />

      {/* ══ HERO ══ */}
      <section className="wd-hero">
        <Particles />

        {/* decorative bg */}
        <div style={{ position: "absolute", inset: 0, zIndex: 0, pointerEvents: "none" }}>
          <div style={{ position: "absolute", top: 0, right: 0, width: "42%", height: "100%", background: "linear-gradient(140deg,rgba(99,102,241,.05),rgba(168,85,247,.04))", clipPath: "polygon(18% 0,100% 0,100% 100%,0% 100%)" }} />
          <div style={{ position: "absolute", top: "18%", right: "6%", width: 300, height: 300, borderRadius: "50%", border: "1px solid rgba(99,102,241,.08)", animation: "wdSpin 38s linear infinite" }} />
          <div style={{ position: "absolute", top: "calc(18% + 28px)", right: "calc(6% + 28px)", width: 244, height: 244, borderRadius: "50%", border: "1px dashed rgba(99,102,241,.05)", animation: "wdSpinR 26s linear infinite" }} />
          <div className="wd-display" style={{ position: "absolute", bottom: -40, left: -10, fontSize: "clamp(100px,18vw,260px)", fontWeight: 900, color: "rgba(255,255,255,.011)", userSelect: "none", lineHeight: 1 }}>WEB</div>
        </div>

        <div className="wd-wrap" style={{ position: "relative", zIndex: 10 }}>

          {/* topbar */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ position: "relative" }}>
                <div style={{ width: 7, height: 7, borderRadius: "50%", background: "#818cf8", animation: "wdPulse 2s infinite" }} />
                <div style={{ position: "absolute", inset: 0, borderRadius: "50%", background: "#818cf8", animation: "wdPing 1.6s ease infinite" }} />
              </div>
              <span className="wd-display" style={{ color: "#818cf8", fontSize: 11, letterSpacing: ".22em", textTransform: "uppercase", fontWeight: 700 }}>SoftCoders / Web Studio</span>
            </div>
            <div className="wd-topbar-r" style={{ alignItems: "center", gap: 8, fontSize: 11, color: "rgba(255,255,255,.2)", letterSpacing: ".15em", textTransform: "uppercase" }}>
              <span>Figma → Production</span>
              <span style={{ width: 22, height: 1, background: "rgba(255,255,255,.12)", display: "inline-block" }} />
              <span>300+ Sites Launched</span>
            </div>
          </div>

          {/* hero grid */}
          <div className="wd-hero-grid">

            {/* LEFT */}
            <div>
              <div className="wd-h1a" style={{ display: "inline-flex", alignItems: "center", gap: 8, border: "1px solid rgba(99,102,241,.28)", background: "rgba(99,102,241,.08)", padding: "7px 16px", borderRadius: 999, fontSize: 12, color: "#a5b4fc", marginBottom: 28, backdropFilter: "blur(10px)" }}>
                <span style={{ width: 5, height: 5, borderRadius: "50%", background: "#818cf8", animation: "wdPulse 2s infinite" }} />
                Award-Winning Web Design & Development
              </div>

              <h1 className="wd-display" style={{ fontSize: "clamp(40px,5.5vw,82px)", fontWeight: 900, lineHeight: .9, letterSpacing: "-2.5px" }}>
                <span className="wd-h1a" style={{ display: "block" }}>Websites</span>
                <span className="wd-h1b" style={{ display: "block" }}>That Win</span>
                <span className="wd-h1c wd-shimmer" style={{ display: "block" }}>Clients.</span>
              </h1>

              <p className="wd-hp" style={{ marginTop: 28, color: "#94a3b8", fontSize: 17, lineHeight: 1.8, maxWidth: 460 }}>
                SoftCoders crafts fast, beautiful, conversion-focused websites for brands that refuse to blend in — from single landing pages to full enterprise platforms.
              </p>

              {/* pills */}
              <div className="wd-pills" style={{ flexWrap: "wrap", gap: 8, marginTop: 20 }}>
                {[["⚡", "< 1s Load"], ["📱", "Mobile-First"], ["🎯", "CRO-Optimised"], ["🔍", "SEO-Ready"]].map(([ic, t], i) => (
                  <span key={i} style={{ padding: "6px 14px", borderRadius: 999, fontSize: 12, fontWeight: 600, border: "1px solid rgba(255,255,255,.09)", color: "#94a3b8", background: "rgba(255,255,255,.04)", backdropFilter: "blur(8px)", animation: `wdFadeUp .6s ease ${.7 + i * .08}s forwards`, opacity: 0 }}>{ic} {t}</span>
                ))}
              </div>

              <div className="wd-hb" style={{ display: "flex", flexWrap: "wrap", gap: 14, marginTop: 34 }}>
                <a
    href="https://wa.me/919254147455?text=Hello%20SoftCoders%2C%20I%20want%20to%20discuss%20my%20project."
    target="_blank"
    rel="noopener noreferrer"
 className="wd-morph wd-cta-p wd-display" style={{ padding: "15px 30px", borderRadius: 14, fontWeight: 800, fontSize: 16, border: "none", color: "#fff", cursor: "pointer", letterSpacing: "-.3px" }}>
                  Start My Website →
                </a>
                <a
    href="/services/web-development" className="wd-cta-s" style={{ padding: "15px 30px", borderRadius: 14, border: "1px solid rgba(255,255,255,.14)", background: "transparent", color: "rgba(255,255,255,.75)", fontSize: 16, cursor: "pointer", fontWeight: 500 }}>
                  View Portfolio
                </a>
              </div>

              <div className="wd-mini-stats wd-hst" style={{ gap: 32, marginTop: 44 }}>
                {[["300+", "Websites"], ["4.9★", "Client Rating"], ["< 1s", "Avg Load"]].map(([v, l], i) => (
                  <div key={i}>
                    <div className="wd-display wd-shimmer" style={{ fontSize: 22, fontWeight: 900 }}>{v}</div>
                    <div style={{ color: "#475569", fontSize: 12, marginTop: 3 }}>{l}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* RIGHT — browser + floating badges */}
            <div style={{ position: "relative", display: "flex", alignItems: "center", justifyContent: "center" }}>

              {/* Lighthouse badge */}
              <div className="wd-badge1 wd-float" style={{ position: "absolute", top: -8, left: -12, zIndex: 20, background: "rgba(6,8,24,.96)", border: "1px solid rgba(99,102,241,.32)", borderRadius: 16, padding: "14px 18px", backdropFilter: "blur(20px)", boxShadow: "0 16px 48px rgba(0,0,0,.55)", animation: "wdFadeUp .7s ease 1s forwards", opacity: 0, minWidth: 158 }}>
                <div style={{ fontSize: 10, color: "#4b5563", marginBottom: 8, textTransform: "uppercase", letterSpacing: ".1em" }}>Lighthouse Score</div>
                <div style={{ display: "flex", gap: 14 }}>
                  {[["Perf", "98", "#34d399"], ["SEO", "100", "#818cf8"], ["A11y", "96", "#f59e0b"]].map(([l, v, c], i) => (
                    <div key={i} style={{ textAlign: "center" }}>
                      <div style={{ fontSize: 20, fontWeight: 900, color: c, fontFamily: "'Syne',sans-serif" }}>{v}</div>
                      <div style={{ fontSize: 9, color: "#6b7280" }}>{l}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* speed badge */}
              <div className="wd-badge2 wd-float2" style={{ position: "absolute", bottom: 20, left: -18, zIndex: 20, background: "rgba(6,8,24,.96)", border: "1px solid rgba(52,211,153,.28)", borderRadius: 14, padding: "12px 16px", backdropFilter: "blur(20px)", animation: "wdFadeUp .7s ease 1.2s forwards", opacity: 0 }}>
                <div style={{ fontSize: 10, color: "#4b5563", marginBottom: 4 }}>Page Load</div>
                <div className="wd-display" style={{ fontSize: 24, fontWeight: 900, color: "#34d399" }}>0.8<span style={{ fontSize: 13, color: "#6b7280", fontFamily: "inherit" }}>s</span></div>
                <div style={{ fontSize: 10, color: "#4b5563" }}>↑ 3× faster than avg</div>
              </div>

              {/* tech badge */}
              <div className="wd-badge3" style={{ position: "absolute", top: 56, right: -12, zIndex: 20, background: "rgba(6,8,24,.96)", border: "1px solid rgba(168,85,247,.28)", borderRadius: 14, padding: "12px 16px", backdropFilter: "blur(20px)", animation: "wdFadeUp .7s ease 1.4s, wdFloat 9s ease-in-out 2.2s infinite", opacity: 0 }}>
                <div style={{ fontSize: 10, color: "#4b5563", marginBottom: 6 }}>Built With</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 5, maxWidth: 118 }}>
                  {["Next.js", "Tailwind", "Framer"].map((t, i) => (
                    <span key={i} style={{ fontSize: 10, padding: "2px 7px", borderRadius: 99, background: "rgba(99,102,241,.14)", color: "#a5b4fc", fontWeight: 600 }}>{t}</span>
                  ))}
                </div>
              </div>

              {/* browser */}
              <div className="wd-float" style={{ width: "100%", maxWidth: 500 }}>
                <BrowserMock />
              </div>
            </div>
          </div>

          {/* marquee strip */}
          <div style={{ borderTop: "1px solid rgba(255,255,255,.04)", paddingTop: 24, marginTop: 0 }}>
            <Marquee items={["Landing Pages", "Corporate Sites", "E-Commerce", "Portfolios", "SaaS Marketing", "Blogs", "Web Apps", "Microsites"]} speed={36} />
          </div>
        </div>
      </section>

      {/* ══ STATS BAND ══ */}
      <div ref={statsRef} className="wd-stats" style={{ background: "rgba(8,10,20,.9)", borderTop: "1px solid rgba(255,255,255,.05)", borderBottom: "1px solid rgba(255,255,255,.05)" }}>
        {[[300, "+", "Sites Delivered"], [98, "", "Avg Lighthouse Score"], [1, "s", "Avg Page Load Target"], [99, "%", "Client Retention"]].map(([end, suf, lbl], i) => (
          <div key={i} style={{ padding: "32px 28px", borderRight: i < 3 ? "1px solid rgba(255,255,255,.04)" : "none", cursor: "default", transition: "background .3s" }}
            onMouseEnter={e => e.currentTarget.style.background = "rgba(99,102,241,.06)"}
            onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
            <div className="wd-display wd-shimmer" style={{ fontSize: 36, fontWeight: 900, letterSpacing: "-1px" }}>
              <Counter end={end} suffix={suf} active={statsOn} />
            </div>
            <div style={{ color: "#4b5563", fontSize: 13, marginTop: 6 }}>{lbl}</div>
          </div>
        ))}
      </div>

      {/* ══ SERVICES ══ */}
      <section className="wd-sec" style={{ borderTop: "1px solid rgba(255,255,255,.04)" }}>
        <div className="wd-wrap">
          <Reveal>
            <p className="wd-display" style={{ color: "#818cf8", fontSize: 11, letterSpacing: ".3em", textTransform: "uppercase", fontWeight: 700, marginBottom: 12 }}>Services</p>
            <h2 className="wd-display" style={{ fontSize: "clamp(28px,4vw,52px)", fontWeight: 900, letterSpacing: "-1.5px", marginBottom: 52 }}>
              What We <span className="wd-shimmer">Build</span>
            </h2>
          </Reveal>

          <div className="wd-svc-layout">
            {/* tabs */}
            <div className="wd-svc-tabs">
              {services.map((s, i) => (
                <div key={i} className={`wd-tab${activeService === i ? " act" : ""}`} onClick={() => setActiveService(i)}>
                  <span style={{ fontSize: 17 }}>{s.icon}</span>
                  <span style={{ fontSize: 13, fontWeight: 600, color: activeService === i ? "#fff" : "#64748b" }}>{s.label}</span>
                </div>
              ))}
            </div>

            {/* content panel */}
            <div style={{ padding: "36px 40px", background: "rgba(8,10,22,.7)" }}>
              <div style={{ display: "flex", alignItems: "flex-start", gap: 16, marginBottom: 20 }}>
                <div style={{ width: 50, height: 50, borderRadius: 14, background: `${svc.color}16`, border: `1px solid ${svc.color}40`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, flexShrink: 0 }}>{svc.icon}</div>
                <h3 className="wd-display" style={{ fontSize: "clamp(18px,2.5vw,28px)", fontWeight: 900, letterSpacing: "-.5px", lineHeight: 1.1 }}>{svc.title}</h3>
              </div>
              <p style={{ color: "#64748b", fontSize: 15, lineHeight: 1.8, maxWidth: 520, marginBottom: 28 }}>{svc.desc}</p>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
                {svc.features.map((f, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 14px", borderRadius: 10, background: `${svc.color}0d`, border: `1px solid ${svc.color}30` }}>
                    <div style={{ width: 5, height: 5, borderRadius: "50%", background: svc.color }} />
                    <span style={{ fontSize: 13, color: "#cbd5e1", fontWeight: 500 }}>{f}</span>
                  </div>
                ))}
              </div>
              <button className="wd-morph wd-cta-p wd-display" style={{ marginTop: 28, padding: "12px 26px", borderRadius: 12, fontWeight: 800, fontSize: 14, border: "none", color: "#fff", cursor: "pointer", letterSpacing: "-.2px" }}>
                Learn More →
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ══ PROCESS ══ */}
      <section className="wd-sec" style={{ borderTop: "1px solid rgba(255,255,255,.05)", background: "rgba(255,255,255,.012)" }}>
        <div className="wd-wrap">
          <Reveal style={{ textAlign: "center", marginBottom: 60 }}>
            <p className="wd-display" style={{ color: "#818cf8", fontSize: 11, letterSpacing: ".3em", textTransform: "uppercase", fontWeight: 700, marginBottom: 12 }}>How We Work</p>
            <h2 className="wd-display" style={{ fontSize: "clamp(28px,4vw,52px)", fontWeight: 900, letterSpacing: "-1.5px" }}>
              From <span className="wd-shimmer">Idea</span> to Live
            </h2>
          </Reveal>
          <div className="wd-proc-grid">
            {process.map((step, i) => (
              <Reveal key={i} delay={i * 90}>
                <div className="wd-step" style={{ padding: 28, borderRadius: 20, background: "rgba(8,10,20,.8)", border: "1px solid rgba(255,255,255,.06)", position: "relative", overflow: "hidden", height: "100%" }}>
                  <div className="wd-display" style={{ position: "absolute", bottom: -14, right: -6, fontSize: 80, fontWeight: 900, color: "rgba(255,255,255,.022)", lineHeight: 1, userSelect: "none" }}>{step.num}</div>
                  <div style={{ width: 34, height: 3, background: `linear-gradient(90deg,${step.color},transparent)`, borderRadius: 99, marginBottom: 20 }} />
                  <h3 className="wd-display" style={{ fontSize: 19, fontWeight: 800, marginBottom: 10, letterSpacing: "-.3px" }}>{step.title}</h3>
                  <p style={{ color: "#64748b", fontSize: 13, lineHeight: 1.7, position: "relative", zIndex: 1 }}>{step.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ══ MARQUEE 2 ══ */}
      <div style={{ padding: "20px 0", borderTop: "1px solid rgba(255,255,255,.04)", borderBottom: "1px solid rgba(255,255,255,.04)" }}>
        <Marquee items={["Next.js", "React", "Tailwind CSS", "Framer Motion", "GSAP", "Three.js", "Webflow", "Shopify", "Sanity", "Vercel"]} speed={50} reverse />
      </div>

      {/* ══ TECH ══ */}
      <section className="wd-sec-sm">
        <div className="wd-wrap">
          <Reveal style={{ textAlign: "center", marginBottom: 40 }}>
            <p className="wd-display" style={{ color: "#334155", fontSize: 11, letterSpacing: ".3em", textTransform: "uppercase", fontWeight: 700 }}>Technology Stack</p>
          </Reveal>
          <div className="wd-tech-row">
            {techs.map((t, i) => (
              <Reveal key={i} delay={i * 30}>
                <span className="wd-tech" style={{ padding: "8px 18px", borderRadius: 999, fontSize: 13, fontWeight: 600, border: "1px solid rgba(255,255,255,.07)", color: "#475569", background: "rgba(255,255,255,.03)" }}>{t}</span>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ══ WHY US ══ */}
      <section className="wd-sec" style={{ borderTop: "1px solid rgba(255,255,255,.05)" }}>
        <div className="wd-wrap">
          <div className="wd-why-grid">
            <Reveal>
              <p className="wd-display" style={{ color: "#818cf8", fontSize: 11, letterSpacing: ".3em", textTransform: "uppercase", fontWeight: 700, marginBottom: 14 }}>Why SoftCoders</p>
              <h2 className="wd-display" style={{ fontSize: "clamp(28px,4vw,52px)", fontWeight: 900, letterSpacing: "-1.5px", lineHeight: 1.05 }}>
                Sites That<br /><span className="wd-shimmer">Actually Work.</span>
              </h2>
              <p style={{ color: "#64748b", marginTop: 22, lineHeight: 1.8, fontSize: 16, maxWidth: 440 }}>
                We obsess over performance, accessibility, and conversion — not just aesthetics. Every site we ship is built to rank, load fast, and turn visitors into customers.
              </p>
              <p style={{ color: "#64748b", marginTop: 14, lineHeight: 1.8 }}>
                Our Figma-to-code workflow means zero translation loss — what you approve in design is exactly what goes live in production.
              </p>
            </Reveal>

            <Reveal delay={160}>
              <div className="wd-why-cards">
                {why.map(([title, sub], i) => (
                  <div key={i} className="wd-why" style={{ padding: "18px 20px", borderRadius: 16, background: "rgba(8,10,20,.8)", border: "1px solid rgba(99,102,241,.1)" }}>
                    <div style={{ width: 26, height: 2, background: "linear-gradient(90deg,#818cf8,#a78bfa)", borderRadius: 99, marginBottom: 12 }} />
                    <div className="wd-display" style={{ fontWeight: 800, fontSize: 13, marginBottom: 5 }}>{title}</div>
                    <div style={{ color: "#475569", fontSize: 12, lineHeight: 1.5 }}>{sub}</div>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ══ CTA ══ */}
     
    </div>
  );
}